import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Alert, AlertDescription } from "@/components/ui/Alert";
import Link from "next/link";
import useCurrentAccount from "@/components/context/useCurrentAccount";
import {
  CurrencyDollarIcon,
  ClockIcon,
  ChartBarIcon,
  UserPlusIcon,
  ArrowRightIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";

export default function ReferralProgramPage() {
  const { account } = useCurrentAccount();
  return (
    <div className="min-h-screen bg-background">
      <Head>
        {generateNextSeo({
          title: "Referral Program - Earn $5/month per customer | Uptime Monitor",
          description:
            "Earn 20% lifetime commission by referring businesses to Uptime Monitor. Live commission protection system with 30-day hold period for stable earnings.",
          canonical: "https://uptime-monitor.io/referral-program",
        })}
      </Head>

      <Header />

      <main className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6">
              Earn $5/month per customer
              <span className="text-primary block">you refer</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Get 20% lifetime commission by sharing Uptime Monitor with businesses that need
              reliable monitoring.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/accounts/login">
                <Button size="lg">
                  Get Your Referral Link
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link href="/docs/referral">
                <Button size="lg" variant="outline">
                  Read Documentation
                </Button>
              </Link>
            </div>
          </div>

          {/* Key Benefits */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <Card className="p-6 text-center">
              <CurrencyDollarIcon className="w-8 h-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold mb-2">20% Lifetime Commission</h3>
              <p className="text-sm text-muted-foreground">
                Earn recurring revenue for every month your referrals stay active
              </p>
            </Card>
            <Card className="p-6 text-center">
              <ClockIcon className="w-8 h-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Start Earning Today</h3>
              <p className="text-sm text-muted-foreground">
                No approval process - get your link and start sharing immediately
              </p>
            </Card>
            <Card className="p-6 text-center">
              <ChartBarIcon className="w-8 h-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Track Your Earnings</h3>
              <p className="text-sm text-muted-foreground">
                Monitor referrals and commission in your dashboard
              </p>
            </Card>
          </div>

          {/* How It Works */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-4">How It Works</h2>
            <p className="text-center text-muted-foreground mb-8">
              Refer a customer on our $24.95/month plan → Earn $4.99/month for life
            </p>

            {/* Commission Protection Notice */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-12 max-w-3xl mx-auto">
              <div className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div className="ml-3">
                  <p className="text-sm text-green-800 dark:text-green-200">
                    <strong>Commission Protection System Active:</strong> All earnings have a 30-day
                    hold period for stability.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="p-6 text-center">
                <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                  1
                </div>
                <h3 className="font-semibold mb-3">Get Your Link</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Sign up for free and find your unique referral link in the dashboard
                </p>
                <Link href="/accounts/login">
                  <Button size="sm" variant="outline">
                    Create Account
                  </Button>
                </Link>
              </Card>

              <Card className="p-6 text-center">
                <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                  2
                </div>
                <h3 className="font-semibold mb-3">Share & Promote</h3>
                <p className="text-sm text-muted-foreground">
                  Share your link with businesses that need uptime monitoring
                </p>
              </Card>

              <Card className="p-6 text-center">
                <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                  3
                </div>
                <h3 className="font-semibold mb-3">Earn Commission</h3>
                <p className="text-sm text-muted-foreground">
                  Get paid 20% monthly commission for every active referral
                </p>
              </Card>
            </div>
          </div>

          {/* Who Should Apply */}
          <Alert className="mb-16">
            <UserPlusIcon className="h-4 w-4" />
            <AlertDescription>
              <strong>Perfect for:</strong> Web developers, IT consultants, DevOps engineers, and
              agencies who work with businesses that need reliable uptime monitoring.
            </AlertDescription>
          </Alert>

          {/* Essential FAQ */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-center mb-8">Quick Questions</h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <Card className="p-6">
                <h3 className="font-semibold mb-2">How much do I earn?</h3>
                <p className="text-sm text-muted-foreground">
                  20% lifetime commission on subscription payments. Example: $4.99/month from a
                  $24.95/month customer.
                </p>
              </Card>
              <Card className="p-6">
                <h3 className="font-semibold mb-2">When do I get paid?</h3>
                <p className="text-sm text-muted-foreground">
                  Monthly via PayPal once you reach $100 minimum. All earnings have a 30-day hold
                  period before payout.
                </p>
              </Card>
              <Card className="p-6">
                <h3 className="font-semibold mb-2">Any restrictions?</h3>
                <p className="text-sm text-muted-foreground">
                  No self-referrals, spam, or brand misuse.{" "}
                  <Link href="/referral-terms" className="text-primary hover:underline">
                    See full terms
                  </Link>
                </p>
              </Card>
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center bg-muted rounded-lg p-12">
            <h2 className="text-2xl font-bold mb-4">Ready to start earning?</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Join professionals already earning recurring income by sharing Uptime Monitor with
              businesses in their network.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {account ? (
                <Link href="/dashboard/referral">
                  <Button size="lg">
                    Go to Referral Dashboard
                    <ArrowRightIcon className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              ) : (
                <Link href="/accounts/login">
                  <Button size="lg">
                    Get Your Referral Link
                    <ArrowRightIcon className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}
              <Link href="/referral-terms">
                <Button size="lg" variant="outline">
                  View Terms
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
